import { Input } from '@/components/design-system/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/design-system/popover';
import { useSearchBar } from '@/features/header/hooks/useSearchBar';
import { cn } from '@/utils/cn';
import { SearchIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { SearchResults } from './SearchResults';

interface SearchBarProps {
  onSearch?: (query: string) => void;
}

export const SearchBar: React.FC<SearchBarProps> = ({ onSearch }) => {
  const { t } = useTranslation();
  const {
    query,
    open,
    isLoading,
    showResults,
    inputRef,
    handleInputChange,
    handleInputFocus,
    handleInputBlur,
    handleResultSelect,
    setIsMouseOverPopover,
    setOpen
  } = useSearchBar({ onSearch });

  const handlePopoverMouseEnter = (): void => {
    setIsMouseOverPopover(true);
  };
  const handlePopoverMouseLeave = (): void => {
    setIsMouseOverPopover(false);

    if (document.activeElement !== inputRef.current) {
      setOpen(false);
    }
  };

  const handleOpenChange = (newOpen: boolean): void => {
    if (!newOpen && document.activeElement === inputRef.current) {
      return;
    }
    setOpen(newOpen);
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <div className="relative">
          <Input
            ref={inputRef}
            type="text"
            placeholder={t('label.search')}
            value={query}
            onChange={handleInputChange}
            onFocus={handleInputFocus}
            onBlur={handleInputBlur}
            className={cn(
              'w-full rounded-lg border pr-10 pl-4 text-sm transition-colors duration-100',
              'border-[#475569] bg-[#334155]/50 text-[#e2e8f0] placeholder:font-light placeholder:text-[#94a3b8]',
              'hover:bg-[#475569]/50 focus:bg-[#475569]/50 focus-visible:border-transparent focus-visible:ring-0 focus-visible:ring-2 focus-visible:ring-[#3b82f6]'
            )}
          />
          <button
            type="button"
            tabIndex={-1}
            className={cn(
              'absolute top-1/2 right-2.5 -translate-y-1/2 rounded p-1 transition-colors hover:bg-neutral-100',
              isLoading && 'opacity-50'
            )}
          >
            <SearchIcon className="size-4 text-neutral-400" />
          </button>
        </div>
      </PopoverTrigger>
      <PopoverContent
        className={cn('mt-3 w-100 border-0 p-0', !showResults && 'hidden')}
        align="center"
        sideOffset={8}
        onMouseEnter={handlePopoverMouseEnter}
        onMouseLeave={handlePopoverMouseLeave}
        onOpenAutoFocus={(e) => {
          e.preventDefault();
        }}
      >
        <SearchResults query={query} onSelect={handleResultSelect} />
      </PopoverContent>
    </Popover>
  );
};
